import { ContentBlockWithImage } from '@dfh-corp-sites/ui'
import { GatsbyImage, withArtDirection } from 'gatsby-plugin-image'
import React, { useMemo } from 'react'

import {
  SanityContentBlockWithImage as ContentBlockWithImageProps,
  SanityCta,
} from '../../../graphql/gatsby'
import { setAnalyticsEvent } from '../../../lib/analytics/state'
import { useSanityRoleBasedContent } from '../../../lib/sanity'
import { getGatsbyImageDataForAsset } from '../../../utils/sanity/image'
import SanityLink, { NormalizedSanityLinkTagged } from '../../global/SanityLink'

const setAnalyticsEventsOnClick = (
  cta: SanityCta,
  link: NormalizedSanityLinkTagged,
  title: string | null | undefined,
) => {
  setAnalyticsEvent({
    event: 'select_content',
    content_type: 'content block',
    item_id: `sanitycontentblock-sanitycontentblockwithimage`,
    title: title || '',
    label_text: cta?.title || '',
    link_destination: link._tag === 'Normalized' ? link.href ?? undefined : link._tag,
    module_name: 'SanityContentBlock',
    module_variant: 'SanityContentBlockWithImage',
  })
}

const SanityContentBlockWithImage = ({
  image,
  imageAlt,
  title,
  subtitle,
  heading,
  content,
  cta,
  ctaAriaLabel,
  layout,
  roleBasedContent,
}: ContentBlockWithImageProps) => {
  const { shouldDisplay } = useSanityRoleBasedContent({ roleBasedContent })
  const imgElt = useMemo((): JSX.Element | undefined => {
    const mobileImageData = getGatsbyImageDataForAsset(image, {
      width: 327,
      height: 416,
      placeholder: 'blurred',
    })
    const tabletImageData = getGatsbyImageDataForAsset(image, {
      width: 338,
      height: 430,
      placeholder: 'blurred',
    })
    const desktopImageData = getGatsbyImageDataForAsset(image, {
      width: 432,
      height: 550,
      placeholder: 'blurred',
    })

    const images =
      mobileImageData && tabletImageData && desktopImageData
        ? withArtDirection(desktopImageData, [
            {
              image: mobileImageData,
              media: '(max-width: 576px)',
            },
            {
              image: tabletImageData,
              media: '(max-width: 768px)',
            },
            {
              image: desktopImageData,
              media: '(min-width: 1024px)',
            },
          ])
        : desktopImageData

    return images ? (
      <div className="relative">
        <GatsbyImage
          image={images}
          loading="eager"
          alt={imageAlt || ''}
          className="w-full h-[416px] md:h-[430px] lg:h-[550px]"
        />
      </div>
    ) : undefined
  }, [image, imageAlt])

  const ctaChildren = cta?.link && cta.link.target && cta.link.target.length > 0 && (
    <SanityLink
      to={cta.link}
      onClick={(_e, normalized) => setAnalyticsEventsOnClick(cta, normalized, title)}
    >
      {cta.title}
    </SanityLink>
  )

  if (!shouldDisplay) return null
  return (
    <ContentBlockWithImage
      image={imgElt}
      title={title || ''}
      subtitle={subtitle || ''}
      heading={heading || ''}
      content={content || ''}
      ctaChildren={ctaChildren}
      ctaAriaLabel={ctaAriaLabel || ''}
      layout={(layout as 'left' | 'right') || 'left'}
    />
  )
}

export default SanityContentBlockWithImage
